import React, { useState } from 'react';
import { Badge } from '../../BasicComponents/Badge';
import type { ExpenseBadgeSize, ExpenseStatus, ExpenseStatusBadgeProps } from './types';

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_MAP: Record<
  ExpenseStatus,
  { variant: 'pending' | 'approved' | 'rejected' | 'hold'; label: string; icon: React.ReactElement }
> = {
  PENDING: {
    variant: 'pending',
    label: 'Pending',
    icon: (
      <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="shrink-0">
        <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.2" />
        <path d="M6 3.5v2.75L7.5 8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    ),
  },
  APPROVED: {
    variant: 'approved',
    label: 'Approved',
    icon: (
      <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="shrink-0">
        <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  REJECTED: {
    variant: 'rejected',
    label: 'Rejected',
    icon: (
      <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="shrink-0">
        <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  HOLD: {
    variant: 'hold',
    label: 'On Hold',
    icon: (
      <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="shrink-0">
        <path d="M4.5 3v6M7.5 3v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
};

const ICON_SIZE: Record<ExpenseBadgeSize, string> = {
  sm: 'w-3 h-3',
  md: 'w-3.5 h-3.5',
};

// ─── Tooltip content builder ──────────────────────────────────────────────────

function buildTooltip(
  status: ExpenseStatus,
  approver?: string | null,
  rejectionReason?: string | null,
  timestamp?: Date | null,
): string | null {
  if (status === 'APPROVED' && approver) {
    const time = timestamp
      ? timestamp.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
      : '';
    return `Approved by ${approver}${time ? ` at ${time}` : ''}`;
  }
  if (status === 'REJECTED') {
    const parts: string[] = [];
    if (approver) parts.push(`Rejected by ${approver}`);
    if (rejectionReason) parts.push(rejectionReason);
    return parts.length > 0 ? parts.join(': ') : null;
  }
  return null;
}

// ─── ExpenseStatusBadge ───────────────────────────────────────────────────────

export function ExpenseStatusBadge({
  status,
  approver = null,
  rejectionReason = null,
  timestamp = null,
  size = 'md',
  className,
}: ExpenseStatusBadgeProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const config = STATUS_MAP[status];
  const tooltipText = buildTooltip(status, approver, rejectionReason, timestamp);

  const iconEl = React.cloneElement(config.icon, {
    className: [ICON_SIZE[size], 'shrink-0'].join(' '),
  });

  const ariaLabel = tooltipText
    ? `Expense status: ${config.label} — ${tooltipText}`
    : `Expense status: ${config.label}`;

  return (
    <span
      className={['relative inline-flex', className ?? ''].filter(Boolean).join(' ')}
      onMouseEnter={() => tooltipText && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onFocus={() => tooltipText && setShowTooltip(true)}
      onBlur={() => setShowTooltip(false)}
      tabIndex={tooltipText ? 0 : undefined}
    >
      <Badge
        variant={config.variant}
        size={size}
        aria-label={ariaLabel}
      >
        {iconEl}
        {config.label}
      </Badge>

      {tooltipText && showTooltip && (
        <span
          role="tooltip"
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-10 whitespace-nowrap max-w-xs rounded-md bg-neutral-900 px-2.5 py-1 text-xs font-normal text-white shadow-md pointer-events-none"
        >
          {tooltipText}
          <span
            className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-neutral-900"
            aria-hidden="true"
          />
        </span>
      )}
    </span>
  );
}
